/**
 * LoyaltyPage — `/loyalty`  (superall-shell-03)
 *
 * Sections
 * ─────────
 * 1. Tier + points hero card  — tier badge with TIER_STYLES colours, total points
 * 2. Progress bar             — points to next tier (if not at max)
 * 3. Expiry callout           — shows expiring30d / expiring90d when non-zero
 * 4. History CTA              — "Open Loyalty App" via handoffToBU to `loyalty` BU
 *
 * The hook calls GET /api/platform/v1/loyalty/summary (richer than /balance).
 * Falls back gracefully if the extended fields are absent.
 */

import * as React from 'react';
import { MotionCard, AnimatedNumber, StaggerList, StaggerItem, FadeIn } from '@workspace/ui';

import { tierStyle } from '../lib/badges';
import { BU_SURFACES, handoffToBU } from '../lib/handoff';
import { PLATFORM_API, apiGetJSON } from '../lib/api';
import { useAuth } from '../providers/AuthProvider';
import { useLang, useT } from '../providers/LangProvider';

// ── types ─────────────────────────────────────────────────────────────────────

interface LoyaltySummaryFull {
  points: number;
  tier: string;
  expiringPoints: number;
  expiringAt: string | null;
  expiring30d?: number;
  expiring90d?: number;
  nextTierPoints?: number; // points needed to reach next tier
  nextTierThreshold?: number; // total threshold for next tier
  enrolled?: boolean;
}

// ── hook ─────────────────────────────────────────────────────────────────────

function useLoyaltySummaryFull() {
  const { token } = useAuth();
  const [data, setData] = React.useState<LoyaltySummaryFull | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  const fetch = React.useCallback(
    (signal?: AbortSignal) => {
      if (!token) return;
      setLoading(true);
      setError(null);
      apiGetJSON<LoyaltySummaryFull>(`${PLATFORM_API}/loyalty/summary`, {
        signal,
      })
        .then(setData)
        .catch((err: unknown) => {
          if ((err as { name?: string }).name === 'AbortError') return;
          setError(err instanceof Error ? err : new Error(String(err)));
        })
        .finally(() => setLoading(false));
    },
    [token]
  );

  React.useEffect(() => {
    if (!token) {
      setData(null);
      return;
    }
    const controller = new AbortController();
    fetch(controller.signal);
    return () => controller.abort();
  }, [token, fetch]);

  return { data, loading, error, refetch: fetch };
}

// ── progress bar ─────────────────────────────────────────────────────────────

function TierProgressBar({
  current,
  needed,
  threshold,
}: {
  current: number;
  needed: number;
  threshold: number;
}) {
  const t = useT();
  const pct =
    threshold > 0 ? Math.min(100, Math.round(((threshold - needed) / threshold) * 100)) : 0;
  return (
    <div className="mt-4">
      <div className="mb-1 flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400">
        <span>{t.loyaltyPage.progressLabel}</span>
        <span>
          {needed.toLocaleString()} {t.loyaltyPage.nextTierPoints}
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={current}
        aria-valuemin={0}
        aria-valuemax={threshold}
        aria-label={t.loyaltyPage.progressLabel}
        className="h-2 w-full overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700"
      >
        <div
          className="h-full rounded-full bg-purple-600 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────

export function LoyaltyPage(): React.ReactElement {
  const t = useT();
  const [lang] = useLang();
  const { token } = useAuth();
  const { data, loading, error, refetch } = useLoyaltySummaryFull();
  const [handingOff, setHandingOff] = React.useState(false);
  const [handoffError, setHandoffError] = React.useState<string | null>(null);

  const loyaltyBU = BU_SURFACES.find((b) => b.id === 'loyalty') ?? null;

  async function openLoyaltyApp() {
    if (!token || !loyaltyBU) return;
    setHandingOff(true);
    setHandoffError(null);
    try {
      await handoffToBU(loyaltyBU, token, import.meta.env.DEV);
    } catch (err) {
      setHandoffError(err instanceof Error ? err.message : t.handoffError);
    } finally {
      setHandingOff(false);
    }
  }

  if (loading) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={t.loyaltyPage.loading}
        className="space-y-3"
      >
        <div className="h-40 animate-pulse rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700" />
        <div className="h-16 animate-pulse rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700" />
        <div className="h-20 animate-pulse rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 dark:bg-red-950/30 px-5 py-6">
        <p className="mb-3 text-sm font-medium text-red-700 dark:text-red-400">
          {t.loyaltyPage.errorTitle}
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="rounded-xl bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
        >
          {t.loyaltyPage.retry}
        </button>
      </div>
    );
  }

  if (!data || data.enrolled === false) {
    return (
      <FadeIn>
        <h1 className="mb-4 text-xl font-bold text-neutral-900 dark:text-neutral-100">
          {t.loyaltyPage.title}
        </h1>
        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-5 py-6 text-center shadow-sm">
          <div className="mb-3 text-4xl" aria-hidden="true">
            ⭐
          </div>
          <h2 className="mb-1 font-semibold text-neutral-900 dark:text-neutral-100">
            {t.loyaltyPage.notEnrolledTitle}
          </h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {t.loyaltyPage.notEnrolledDetail}
          </p>
        </div>
      </FadeIn>
    );
  }

  const ts = tierStyle(data.tier);
  const tierLabel = ts.label[lang] ?? data.tier;
  const atMaxTier = !data.nextTierPoints && !data.nextTierThreshold;

  // Rewards (mocked from data for stagger demo)
  const rewards = [
    { id: 'expiry', label: data.expiring30d ? `${data.expiring30d} pts expiring soon` : null },
    { id: 'expiry90', label: data.expiring90d ? `${data.expiring90d} pts expiring in 90d` : null },
  ].filter((r) => r.label !== null);

  return (
    <FadeIn>
      <h1 className="mb-4 text-xl font-bold text-neutral-900 dark:text-neutral-100">
        {t.loyaltyPage.title}
      </h1>

      {/* ── Tier + points hero ───────────────────────── */}
      <MotionCard hoverScale={1.01} tapScale={0.99} className="mb-4 rounded-2xl">
        <section
          aria-label={`${t.loyaltyPage.tierLabel}: ${tierLabel}`}
          className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-5 py-5 shadow-sm"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                {t.loyaltyPage.pointsLabel}
              </p>
              <p className="mt-1 text-4xl font-bold tabular-nums text-neutral-900 dark:text-neutral-100">
                <AnimatedNumber value={data.points} />
              </p>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${ts.bg} ${ts.text}`}>
              {tierLabel}
            </span>
          </div>

          {atMaxTier ? (
            <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
              {t.loyaltyPage.tierMaxReached}
            </p>
          ) : data.nextTierPoints !== undefined && data.nextTierThreshold !== undefined ? (
            <TierProgressBar
              current={data.points}
              needed={data.nextTierPoints}
              threshold={data.nextTierThreshold}
            />
          ) : null}
        </section>
      </MotionCard>

      {/* ── Expiry warnings ──────────────────────────── */}
      {(data.expiring30d ?? data.expiringPoints ?? 0) > 0 && (
        <section
          aria-label={t.loyaltyPage.expirySection}
          className="mb-4 rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-5 py-4"
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400 mb-2">
            {t.loyaltyPage.expirySection}
          </p>
          {data.expiring30d !== undefined && data.expiring30d > 0 && (
            <p className="text-sm text-amber-800 dark:text-amber-300">
              <span className="font-semibold">{data.expiring30d.toLocaleString()}</span> pts —{' '}
              {t.loyaltyPage.expiring30d}
            </p>
          )}
          {data.expiring90d !== undefined && data.expiring90d > 0 && (
            <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
              <span className="font-semibold">{data.expiring90d.toLocaleString()}</span> pts —{' '}
              {t.loyaltyPage.expiring90d}
            </p>
          )}
          {data.expiring30d === undefined && data.expiringPoints > 0 && (
            <p className="text-sm text-amber-800 dark:text-amber-300">
              <span className="font-semibold">{data.expiringPoints.toLocaleString()}</span> pts —{' '}
              {t.loyaltyPage.expiring30d}
              {data.expiringAt
                ? ` · ${new Date(data.expiringAt).toLocaleDateString('en-SA', { month: 'short', day: 'numeric' })}`
                : ''}
            </p>
          )}
        </section>
      )}

      {/* ── Rewards stagger list ─────────────────────── */}
      {rewards.length > 0 && (
        <section className="mb-4">
          <StaggerList className="space-y-2">
            {rewards.map((reward) => (
              <StaggerItem key={reward.id}>
                <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">
                  {reward.label}
                </div>
              </StaggerItem>
            ))}
          </StaggerList>
        </section>
      )}

      {/* ── History CTA ──────────────────────────────── */}
      <section
        aria-label={t.loyaltyPage.historyTitle}
        className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-5 py-5 shadow-sm"
      >
        <h2 className="mb-1 font-semibold text-neutral-900 dark:text-neutral-100">
          {t.loyaltyPage.historyTitle}
        </h2>
        <p className="mb-4 text-sm text-neutral-500 dark:text-neutral-400">
          {loyaltyBU ? t.loyaltyPage.historyComingSoonDetail : t.loyaltyPage.historyComingSoon}
        </p>

        {handoffError && <p className="mb-3 text-xs text-red-600">{handoffError}</p>}

        {loyaltyBU && (
          <button
            type="button"
            disabled={handingOff}
            onClick={() => void openLoyaltyApp()}
            className="w-full rounded-xl bg-purple-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
          >
            {handingOff ? t.opening : t.loyaltyPage.viewInLoyaltyApp}
          </button>
        )}
      </section>
    </FadeIn>
  );
}
